/**
 * 캠핑 허브 기간 요금 헬퍼 — 캠핑장 시트의 장비 카드·캠핑장 목록 행이 쓴다.
 *
 * **새 가격 규칙을 만들지 않는다.** 대여일수는 장바구니와 같은
 * `calculateRentalDays`(픽업일·반납일 포함, 10/3 → 10/5 = 3일 = 2박 3일)이고,
 * 총액은 바이크 허브와 같은 `periodTotal`(그 일수 구간의 일 단가 × 일수 =
 * `useBookingCart` 의 `getDailyV2RateForDays(rentalDays) × rentalDays`)이다.
 * 세부 상품이 여럿이면 가장 싼 것 — 상세에서 기본 선택을 바꾸기 전의 "~부터" 값.
 */

import type { Product } from "@/types";
import { calculateRentalDays } from "@/lib/utils/pricing";
import { periodTotal } from "@/lib/mobility/pricing";

/**
 * 캠핑 허브 데이터셋이 싣는 기간 구간 상한(일) — `lib/api/station-hub` 가 캠핑이면
 * 1~이 값까지 **모든** 구간을 가져온다. 그 안에서는 어떤 N 이든 상세와 같은 구간이
 * 선택되고, 넘으면 구간이 빠져 있을 수 있어 기간가를 내지 않는다.
 */
export const CAMPING_TIER_MAX_DAYS = 30;

/** 날짜를 고르지 않았을 때의 기준 기간 — 1박 2일(기본 24시간 요금과 같은 값). */
export const CAMPING_DEFAULT_DAYS = 2;

/** "YYYY-MM-DD" 두 개 → 대여일수(포함). 하나라도 없거나 뒤집혔으면 null. */
export function campingRentalDays(
  checkin: string | null | undefined,
  checkout: string | null | undefined,
): number | null {
  if (!checkin || !checkout) return null;
  const days = calculateRentalDays(
    new Date(`${checkin}T00:00:00`),
    new Date(`${checkout}T00:00:00`),
  );
  return days > 0 ? days : null;
}

export interface CampingPrice {
  /** 표시가(마크업 반영) 총액 */
  total: number;
  /** 총액이 가리키는 대여일수 */
  days: number;
  /** 고른 기간이 아니라 기준 기간(1박 2일)의 "~부터" 값인가 */
  isDefault: boolean;
}

/**
 * 장비 한 개의 카드 가격.
 * - 고른 기간이 있고 구간 상한 안이면 그 기간 총액.
 * - 날짜가 없거나 상한을 넘으면 기준 기간(1박 2일) 총액을 "~부터"로.
 * - 가격이 없으면 null(0 을 보여 주지 않는다).
 */
export function campingPrice(
  product: Product,
  rentalDays: number | null,
): CampingPrice | null {
  // 시간대 상품은 대표가가 시간 옵션 묶음 가격(예: 1박 2일)이라 1일가 × 일수가 성립하지 않는다(10-15)
  if (product.rental_unit === "timeslot") return null;
  if (rentalDays != null && rentalDays <= CAMPING_TIER_MAX_DAYS) {
    const total = periodTotal(product, rentalDays);
    if (total != null && total > 0) {
      return { total, days: rentalDays, isDefault: false };
    }
  }
  const total = periodTotal(product, CAMPING_DEFAULT_DAYS);
  if (total == null || !(total > 0)) return null;
  return { total, days: CAMPING_DEFAULT_DAYS, isDefault: true };
}

/**
 * 캠핑장 목록 행의 "~부터" — 장비들 중 가장 싼 카드 가격. 없으면 null.
 * `days` 는 그 가격이 가리키는 대여일수 — 행의 "/ 1박 2일" 문구에 쓴다.
 */
export function lowestCampingPrice(
  products: readonly Product[],
  rentalDays: number | null,
): { total: number; currency: string | undefined; days: number } | null {
  let best: { total: number; currency: string | undefined; days: number } | null =
    null;
  for (const product of products) {
    const price = campingPrice(product, rentalDays);
    if (price && (!best || price.total < best.total)) {
      best = { total: price.total, currency: product.currency, days: price.days };
    }
  }
  return best;
}

/**
 * "YYYY-MM-DD" → "10월 3일" / "Oct 3" / "10月3日". 달력 날짜라 UTC 로 고정해
 * 서버·브라우저 시간대가 달라도 같은 글자가 나온다(하이드레이션 일치).
 */
export function formatCampingDate(date: string, locale: string): string {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}
